import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { hexToHsva, hsvaToHex } from '@uiw/color-convert'
import {
  toSiteSettings,
  useRestaurantSettingsStore
} from '@/features/restaurant-settings'
import { useGoogleLocationQuery } from '@/features/google-location'
import { GOOGLE_LOCATION_ANCHOR, useSettingsModal } from '@/features/settings'
import Button from '@/shared/components/Button/Button'
import CheckBox from '@/shared/components/CheckBox/CheckBox'
import ColorPicker from '@/shared/components/ColorPicker/ColorPicker'
import TextField from '@/shared/components/TextField/TextField'
import { DEFAULT_BRAND_COLOR, THEME_FONT_PAIRS } from '../../config'
import { MAP_HEIGHTS, MapHeight } from '../../schema/elements'
import { SiteSettings } from '../../schema/site'
import { sectionHeading } from '../../templates'
import { useRestaurantRooms } from '../../hooks/useRestaurantRooms'
import { roomPlanFrom } from '../../utils/roomPlan'
import { Theme } from '../../render/theme'
import FontFamilyFields from '../FontFamilyFields/FontFamilyFields'
import PageColors from '../PropertiesPanel/PageColors'
import TokenSelect from '../PropertiesPanel/TokenSelect'
import TemplatePicker from '../TemplatePicker/TemplatePicker'
import SectionsEditor, {
  sectionCardId
} from '../SectionsEditor/SectionsEditor'
import styles from './SiteSettingsForm.module.scss'

interface SiteSettingsFormProps {
  initial: SiteSettings
  theme: Theme
  language: string
  /**
   * Every change, as it happens.
   *
   * This form does not save. It reports what has been typed and the page it
   * sits in decides what to do with it, which is what lets the save button live
   * OUTSIDE the scrolling column: the button cannot be inside the thing it has
   * to stay visible on top of.
   */
  onChange?: (settings: SiteSettings, theme: Theme) => void
  /** Whether the venue has dishes to put in the menu section. */
  hasMenu?: boolean
}

/**
 * The whole of what an owner does to their page: pick a template, fill in the
 * facts, decide about the sections, add a block or two of their own. The page
 * on the right is always what these answers produce.
 */
function SiteSettingsForm(props: SiteSettingsFormProps) {
  const {
    initial,
    theme: initialTheme,
    language,
    onChange,
    hasMenu = false
  } = props
  const { t } = useTranslation()

  const [settings, setSettings] = useState<SiteSettings>(initial)
  const [theme, setTheme] = useState<Theme>(initialTheme)

  /*
    Open from the start for a page whose faces or colours were already chosen by
    hand, whatever chose them: a page that does not use its pair and its brand
    colour must not look as if it does, and the block is where that is said.
  */
  const [advanced, setAdvanced] = useState(
    Boolean(initialTheme.fonts || initialTheme.colors)
  )

  // Reported after the render that shows it, never during one: the parent
  // turns this into a rebuilt document, and setting its state mid-render of
  // this component is the "cannot update while rendering" warning.
  useEffect(() => {
    onChange?.(settings, theme)
  }, [settings, theme, onChange])

  const patch = (values: Partial<SiteSettings>) =>
    setSettings(current => ({ ...current, ...values }))

  const openSettings = useSettingsModal(s => s.open)

  /*
    The facts are edited in the settings modal, over this page, which therefore
    stays mounted; leaving for another page used to be what refreshed them. So
    a saved change is taken in here, merged over what was typed in this form
    the same way the page seeds it, rather than rebuilding the form from scratch.
  */
  const restaurantSettings = useRestaurantSettingsStore(s => s.settings)
  // Connected or changed in the same modal, and read after this form mounts.
  const googleLocation = useGoogleLocationQuery().data
  // Connected, to a place Google has since dropped: the snapshot leaves it
  // out like no connection at all, but the way out is picking it again.
  const needsReselect = googleLocation?.status === 'needs_reselect'
  const factsSeeded = useRef(false)
  useEffect(() => {
    if (!factsSeeded.current) {
      factsSeeded.current = true
      return
    }
    setSettings(current =>
      toSiteSettings(restaurantSettings, current, googleLocation)
    )
  }, [restaurantSettings, googleLocation])

  /*
    The facts as one line each, for the block that shows them. Read straight off
    the snapshot in the document rather than from the settings store: what this
    page renders IS the snapshot, and showing the store's newer values here would
    hide exactly the staleness the page warns about.
  */
  const formattedAddress = [
    settings.street,
    [settings.postalCode, settings.city].filter(Boolean).join(' ')
  ]
    .filter(Boolean)
    .join(', ')

  const contactLine = [settings.phone, settings.email].filter(Boolean).join(' · ')

  const { map, review } = settings.sections
  const patchSections = (values: Partial<SiteSettings['sections']>) =>
    patch({ sections: { ...settings.sections, ...values } })

  const { rooms, tables, isLoading: roomsLoading } = useRestaurantRooms()

  /*
    A venue that has drawn a plan gets it on the page, the same way it gets its
    opening hours and its map without asking: the template's rule is that a
    section appears when there is something to put in it. Leaving it off by
    default meant the block existed and nobody ever saw it, because nothing in
    the preview said there was a choice to make.

    Once only, and only while nothing has been chosen. After that the select
    owns the value, or picking "do not show a room" would be undone on the very
    next render.
  */
  const roomsSeeded = useRef(false)

  useEffect(() => {
    if (roomsSeeded.current || roomsLoading || rooms.length === 0) return
    roomsSeeded.current = true
    if (settings.reservationRooms) return
    patch({ reservationRooms: rooms.map(room => roomPlanFrom(room, tables)) })
    // `patch` and `settings` are read fresh on every render; this runs on the
    // one where the rooms arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, tables, roomsLoading])

  return (
    <div className={styles.form}>
      {/*
        First, because everything under it is read in the light of it: the
        sections below are the chosen template's, in its order.
      */}
      <section className={styles.section}>
        <p className={`title-bold-small ${styles.sectionTitle}`}>
          {t('WEBSITE.TEMPLATE.PICK')}
        </p>
        <TemplatePicker
          value={settings.templateId}
          onChange={templateId => patch({ templateId })}
        />
      </section>

      {/*
        The venue's own facts, shown but not editable.

        They are owned by the restaurant sections of the settings modal, and
        this document keeps a snapshot of them
        (`restaurant-settings/adapters/toSiteSettings`). Two forms asking for
        one address is how the two answers start to differ.
      */}
      <section className={styles.section}>
        <p className={`title-bold-small ${styles.sectionTitle}`}>
          {t('WEBSITE.SETTINGS.VENUE_FACTS')}
        </p>
        <div className={styles.facts}>
          <p className={`body-medium ${styles.factLine}`}>
            {settings.name || t('WEBSITE.SETTINGS.VENUE_FACTS_MISSING')}
          </p>
          {formattedAddress && (
            <p className={`body-small ${styles.factMeta}`}>{formattedAddress}</p>
          )}
          {contactLine && (
            <p className={`body-small ${styles.factMeta}`}>{contactLine}</p>
          )}
        </div>
        <p className={`body-small ${styles.hint}`}>
          {t('WEBSITE.SETTINGS.VENUE_FACTS_HINT')}
        </p>
        <Button
          label={t('WEBSITE.SETTINGS.VENUE_FACTS_EDIT')}
          variant="text"
          color="primary"
          smallFont
          icon="las la-pen icon-medium"
          onClick={() => openSettings('restaurant')}
        />
      </section>

      {/*
        The map and the review button have no cards in the list of sections:
        both come from the venue's Google place, connected in the settings,
        so they sit together with a word about that place. The group carries
        the map's id and the review's part its own, so a click on either in
        the preview brings it into view.
      */}
      <section id={sectionCardId('map')} className={styles.section}>
        <p className={`title-bold-small ${styles.sectionTitle}`}>
          {t('WEBSITE.SETTINGS.GOOGLE')}
        </p>
        <p className={`body-small ${styles.hint}`}>
          {settings.googleLocation && t('WEBSITE.SETTINGS.GOOGLE_CONNECTED')}
          {!settings.googleLocation &&
            t(
              needsReselect
                ? 'WEBSITE.SETTINGS.GOOGLE_RESELECT'
                : 'WEBSITE.SETTINGS.GOOGLE_MISSING'
            )}
        </p>
        {!settings.googleLocation && (
          <Button
            label={
              needsReselect
                ? t('GOOGLE_LOCATION.RESELECT')
                : t('WEBSITE.SETTINGS.GOOGLE_CONNECT')
            }
            variant="text"
            color="primary"
            smallFont
            icon="las la-map-marker-alt icon-medium"
            onClick={() => openSettings('restaurant', GOOGLE_LOCATION_ANCHOR)}
          />
        )}
        <CheckBox
          label={t('WEBSITE.SETTINGS.SHOW_MAP')}
          checked={map.enabled}
          value={map.enabled}
          onChange={value => patchSections({ map: { ...map, enabled: value } })}
        />
        {map.enabled && (
          <TokenSelect
            label={t('WEBSITE.SETTINGS.MAP_HEIGHT')}
            value={map.height}
            options={[...MAP_HEIGHTS]}
            translate={value => t(`WEBSITE.SETTINGS.MAP_HEIGHT_${value}`)}
            onChange={value =>
              patchSections({ map: { ...map, height: value as MapHeight } })
            }
          />
        )}
        <div id={sectionCardId('review')} className={styles.group}>
          <CheckBox
            label={t('WEBSITE.SETTINGS.SHOW_REVIEW')}
            checked={review.enabled}
            value={review.enabled}
            onChange={value =>
              patchSections({ review: { ...review, enabled: value } })
            }
          />
          {review.enabled && (
            <TextField
              label={t('WEBSITE.SETTINGS.REVIEW_LABEL')}
              // What the page says when this is left empty.
              placeholder={sectionHeading(language, 'review')}
              focused
              value={review.label?.[language] ?? ''}
              onChange={event =>
                patchSections({
                  review: {
                    ...review,
                    label: { ...review.label, [language]: event.target.value }
                  }
                })
              }
            />
          )}
        </div>
      </section>

      {/*
        The chosen template's sections, one card each in the order they take
        on the page, with the owner's own blocks between them where they fall
        on the page. Together these are the whole of what can be done to the
        page's layout.
      */}
      <SectionsEditor
        settings={settings}
        language={language}
        patch={patch}
        hasMenu={hasMenu}
      />

      <section className={styles.section}>
        <p className={`title-bold-small ${styles.sectionTitle}`}>
          {t('WEBSITE.SETTINGS.LOOK')}
        </p>
        {/*
          One colour, and the page's whole palette comes out of it: backgrounds,
          the muted and highlighted row fills, the border and both text colours.
          The text is darkened until it clears WCAG AA against every one of those
          surfaces, so there is no colour here that produces an unreadable page.
        */}
        <ColorPicker
          label={t('WEBSITE.SETTINGS.BRAND_COLOR')}
          color={hexToHsva(theme.brandColor ?? DEFAULT_BRAND_COLOR)}
          setColor={next =>
            setTheme(current => ({ ...current, brandColor: hsvaToHex(next) }))
          }
        />
        <p className={`body-small ${styles.hint}`}>
          {t('WEBSITE.SETTINGS.BRAND_COLOR_HINT')}
        </p>
        <TokenSelect
          label={t('WEBSITE.SETTINGS.FONTS')}
          value={theme.fontPairId}
          options={[...THEME_FONT_PAIRS]}
          translate={value => t(`WEBSITE.THEME_FONTS.${value}`)}
          onChange={value =>
            setTheme(current => ({
              ...current,
              fontPairId: value as Theme['fontPairId'],
              // Picking a pair means picking both of its faces, so a family
              // chosen by hand earlier stops overriding it.
              fonts: undefined
            }))
          }
        />
        {/*
          Everything above this line is a choice between things somebody has
          already made work together. Under it is the catalogue, which is where
          a page can be made to look like nothing else or like a ransom note,
          so it is asked for rather than offered.
        */}
        <CheckBox
          label={t('WEBSITE.SETTINGS.ADVANCED')}
          checked={advanced}
          value={advanced}
          onChange={value => {
            setAdvanced(value)
            /*
              Closed means the pair and the brand colour decide again. Left
              behind, a family or a colour chosen here would go on setting the
              page from a box nobody could see.
            */
            if (!value) {
              setTheme(current => ({
                ...current,
                fonts: undefined,
                colors: undefined
              }))
            }
          }}
        />
        <p className={`body-small ${styles.hint}`}>
          {t('WEBSITE.SETTINGS.ADVANCED_HINT')}
        </p>
        {advanced && (
          <>
            <FontFamilyFields theme={theme} onChange={setTheme} />
            {/*
              Every colour of the page, one picker each, the same control the
              builder's panel uses. There is no undo history to protect here,
              which is what `onPreview` exists for there, so a drag of the wheel
              and the release at the end of it are the same thing: the form's
              state, and the page beside it, follow the pointer.
            */}
            <PageColors
              theme={theme}
              onChange={setTheme}
              onPreview={setTheme}
              onPreviewEnd={() => undefined}
            />
          </>
        )}
      </section>
    </div>
  )
}

export default SiteSettingsForm
